/**
 * Onboarding schema — single source of truth for onboarding questions across
 * web and mobile. Replaces the 985-line mobile OnboardingModal + 992-line web
 * PersonalizationModal divergence where adding a new question required edits
 * in two places (and they often drifted).
 *
 * Each step declares:
 *   - id            stable identifier (used for analytics + step navigation)
 *   - field         canonical preference key (matches preferenceSchema.js)
 *   - type          input kind: 'text' | 'single' | 'multi' | 'tags' | 'boolean'
 *   - question      headline shown to the user
 *   - helper?       sub-text below the question
 *   - placeholder?  for text inputs
 *   - options?      array of {value, label, description?, icon?} for single/multi
 *   - validate?     custom validator returning {valid, error?}
 *   - conditional?  function(prefs) → boolean to show/hide the step
 *   - skippable     defaults true; set false for required fields
 *   - max?          for tags: maximum number of items (default 50)
 *
 * Renderers:
 *   - Web:   web/src/components/auth/PersonalizationModal.jsx
 *   - Mobile: mobile/src/components/OnboardingModal.js
 *
 * Both use the same schema; they differ only in the React/RN component
 * primitives (button vs Pressable, etc.) and the styling system.
 */

import {
  PACE_VALUES,
  BUDGET_VALUES,
  CABIN_CLASS_VALUES,
  COMPANION_VALUES,
  COMPANION_BASE_VALUES,
  COMPANION_MODIFIER_VALUES,
} from './preferenceSchema.js';

// ─── REUSABLE OPTION SETS ───────────────────────────────────────────────────

export const PACE_OPTIONS = [
  { value: 'relaxed',  label: 'Relaxed',  description: 'Few activities per day, lots of downtime' },
  { value: 'moderate', label: 'Balanced', description: 'Mix of activities and free time' },
  { value: 'packed',   label: 'Packed',   description: 'See and do as much as possible' },
];

export const BUDGET_OPTIONS = [
  { value: 'budget',       label: 'Budget',       description: 'Hostels, street food, public transit' },
  { value: 'moderate',     label: 'Moderate',     description: '3★ hotels, mid-range meals' },
  { value: 'luxury',       label: 'Luxury',       description: '5★ hotels, fine dining' },
  { value: 'ultra-luxury', label: 'Ultra-Luxury', description: 'No spending limit; private flights, suites' },
  { value: 'custom',       label: 'Custom',       description: 'Specify a daily target' },
];

// Multi-select companion options. UI components should treat the `base: true`
// entries as mutually exclusive (you're either solo OR a couple, not both),
// and `base: false` entries as additive modifiers (kids, elderly, pets, etc.)
// that stack on top of a base. Stored as a single array on `preferences.companions`.
export const COMPANION_OPTIONS = [
  // Base — exactly one selected at a time
  { value: 'solo',       label: 'Solo',         description: 'Solo travel spots',         base: true },
  { value: 'couple',     label: 'Couple',       description: 'Romantic activities',       base: true },
  { value: 'family',     label: 'Family',       description: 'Family-friendly places',     base: true },
  { value: 'friends',    label: 'Friends',      description: 'Group experiences',         base: true },
  { value: 'group',      label: 'Group',        description: 'Larger group itineraries',  base: true },
  { value: 'business',   label: 'Business',     description: 'Work-trip friendly',        base: true },
  // Modifiers — additive, stack on top of base
  { value: 'kids',       label: 'With Kids',     description: 'Kid-friendly activities',   base: false },
  { value: 'teens',      label: 'With Teens',    description: 'Teen attractions',          base: false },
  { value: 'elderly',    label: 'With Elderly',  description: 'Accessible options',        base: false },
  { value: 'wheelchair', label: 'Wheelchair',    description: 'Wheelchair accessible',     base: false },
  { value: 'pets',       label: 'With Pets',     description: 'Pet-friendly places',       base: false },
];

export const COMPANION_BASE_OPTIONS = COMPANION_OPTIONS.filter(o => o.base);
export const COMPANION_MODIFIER_OPTIONS = COMPANION_OPTIONS.filter(o => !o.base);

export const CABIN_CLASS_OPTIONS = [
  { value: 'ECONOMY',         label: 'Economy' },
  { value: 'PREMIUM_ECONOMY', label: 'Premium Economy' },
  { value: 'BUSINESS',        label: 'Business' },
  { value: 'FIRST',           label: 'First' },
];

export const INTEREST_OPTIONS = [
  // Categories aligned with the canonical interests list referenced in mobile
  // store's `_CANONICAL_INTERESTS_SET` migration.
  { value: 'Architecture',         label: 'Architecture' },
  { value: 'Art & Galleries',      label: 'Art & Galleries' },
  { value: 'Beaches',              label: 'Beaches' },
  { value: 'Coffee Culture',       label: 'Coffee Culture' },
  { value: 'Cultural Experiences', label: 'Cultural Experiences' },
  { value: 'Fine Dining',          label: 'Fine Dining' },
  { value: 'History',              label: 'History' },
  { value: 'Hiking & Nature',      label: 'Hiking & Nature' },
  { value: 'Live Music',           label: 'Live Music' },
  { value: 'Local Markets',        label: 'Local Markets' },
  { value: 'Museums',              label: 'Museums' },
  { value: 'Nightlife',            label: 'Nightlife' },
  { value: 'Photography',          label: 'Photography' },
  { value: 'Shopping',             label: 'Shopping' },
  { value: 'Spas & Wellness',      label: 'Spas & Wellness' },
  { value: 'Street Food',          label: 'Street Food' },
  { value: 'Surfing',              label: 'Surfing' },
  { value: 'Wildlife',             label: 'Wildlife' },
  { value: 'Wine & Cocktails',     label: 'Wine & Cocktails' },
];

export const DIETARY_OPTIONS = [
  { value: 'vegetarian',   label: 'Vegetarian' },
  { value: 'vegan',        label: 'Vegan' },
  { value: 'pescatarian',  label: 'Pescatarian' },
  { value: 'kosher',       label: 'Kosher' },
  { value: 'halal',        label: 'Halal' },
  { value: 'gluten-free',  label: 'Gluten-Free' },
  { value: 'dairy-free',   label: 'Dairy-Free' },
  { value: 'nut-allergy',  label: 'Nut Allergy' },
  { value: 'shellfish-allergy', label: 'Shellfish Allergy' },
];

export const ACCESSIBILITY_OPTIONS = [
  { value: 'wheelchair',         label: 'Wheelchair access' },
  { value: 'limited-mobility',   label: 'Limited mobility' },
  { value: 'visual-impairment',  label: 'Visual impairment' },
  { value: 'hearing-impairment', label: 'Hearing impairment' },
  { value: 'service-animal',     label: 'Service animal' },
  { value: 'sensory-friendly',   label: 'Sensory-friendly' },
];

export const SAFETY_CONSIDERATION_OPTIONS = [
  { value: 'solo-female',    label: 'Solo female traveler' },
  { value: 'lgbtq-friendly', label: 'LGBTQ+ friendly destinations' },
  { value: 'family-friendly', label: 'Family-friendly only' },
  { value: 'low-crime',      label: 'Low crime priority' },
  { value: 'medical-access', label: 'Easy medical access' },
];

// ─── ONBOARDING STEPS ───────────────────────────────────────────────────────

export const ONBOARDING_STEPS = [
  {
    id: 'home_city',
    field: 'homeCity',
    type: 'text',
    question: 'Where are you traveling from?',
    helper: 'We use this to estimate flight times and budgets.',
    placeholder: 'New York, San Francisco, London',
    skippable: false,
    validate: (value) => {
      const trimmed = (value || '').trim();
      if (!trimmed) return { valid: false, error: 'Home city is required' };
      if (trimmed.length < 2) return { valid: false, error: 'Please enter a real city name' };
      return { valid: true };
    },
  },
  {
    id: 'passport',
    field: 'passportCountry',
    type: 'tags',
    question: 'Which passports do you hold?',
    helper: 'We check entry requirements against your citizenship before suggesting destinations.',
    placeholder: 'United States, Canada',
    max: 5,
  },
  {
    id: 'pace',
    field: 'pace',
    type: 'single',
    question: 'How do you like to travel?',
    options: PACE_OPTIONS,
  },
  {
    id: 'budget',
    field: 'budget',
    type: 'single',
    question: 'What’s your typical budget level?',
    options: BUDGET_OPTIONS,
  },
  {
    id: 'companions',
    field: 'companions',
    type: 'multi',
    question: 'Who do you usually travel with?',
    helper: 'Pick a base type (solo/couple/family/etc) plus any modifiers — kids, elderly, pets, etc.',
    options: COMPANION_OPTIONS,
  },
  {
    id: 'interests',
    field: 'interests',
    type: 'multi',
    question: 'What kinds of things do you enjoy?',
    helper: 'Pick as many as apply — we’ll prioritize matching activities.',
    options: INTEREST_OPTIONS,
  },
  {
    id: 'dietary',
    field: 'dietary',
    type: 'multi',
    question: 'Any dietary restrictions?',
    helper: 'We’ll filter restaurant suggestions accordingly.',
    options: DIETARY_OPTIONS,
    skippable: true,
  },
  {
    id: 'accessibility',
    field: 'accessibilityNeeds',
    type: 'multi',
    question: 'Accessibility needs?',
    helper: 'Optional — helps us pick venues with the right accommodations.',
    options: ACCESSIBILITY_OPTIONS,
    skippable: true,
  },
  {
    id: 'safety',
    field: 'safetyConsiderations',
    type: 'multi',
    question: 'Any safety considerations to factor in?',
    helper: 'Optional — affects destination recommendations and warnings.',
    options: SAFETY_CONSIDERATION_OPTIONS,
    skippable: true,
  },
  {
    id: 'cabin_class',
    field: 'cabinClass',
    type: 'single',
    question: 'Preferred cabin class for flights?',
    options: CABIN_CLASS_OPTIONS,
    skippable: true,
  },
  {
    id: 'liked_places',
    field: 'likedPlaces',
    type: 'tags',
    question: 'Dream destinations?',
    helper: 'Add cities or countries you want to visit. We’ll prioritize them in recommendations.',
    placeholder: 'Tokyo, Patagonia, Iceland',
    max: 30,
    skippable: true,
  },
  {
    id: 'avoid_places',
    field: 'avoidPlaces',
    type: 'tags',
    question: 'Anywhere you’d rather skip?',
    helper: 'We’ll exclude these from recommendations.',
    placeholder: 'Las Vegas, cruise ports',
    max: 30,
    skippable: true,
  },
  {
    id: 'travel_notes',
    field: 'travelNotes',
    type: 'text',
    question: 'Anything else we should know?',
    helper: 'Free text. Mention quirks, preferences, or things to avoid.',
    placeholder: 'I prefer mornings off, hate crowded museums, etc.',
    multiline: true,
    skippable: true,
  },
];

// ─── HELPERS ────────────────────────────────────────────────────────────────

/**
 * Find a step definition by id.
 */
export function getStep(id) {
  return ONBOARDING_STEPS.find(s => s.id === id) || null;
}

/**
 * Validate a step's value. Returns { valid, error? }. Defers to step.validate
 * when present, otherwise applies type-specific defaults.
 */
export function validateStep(step, value) {
  if (step.validate) return step.validate(value);

  switch (step.type) {
    case 'text':
      if (step.skippable === false && !(value || '').trim()) {
        return { valid: false, error: `${step.question} is required` };
      }
      return { valid: true };
    case 'single':
      if (step.skippable === false && !value) {
        return { valid: false, error: 'Please pick one' };
      }
      if (value && step.options && !step.options.some(o => o.value === value)) {
        return { valid: false, error: 'Invalid choice' };
      }
      return { valid: true };
    case 'multi':
    case 'tags':
      if (!Array.isArray(value)) return { valid: false, error: 'Expected an array' };
      if (step.max && value.length > step.max) {
        return { valid: false, error: `Maximum ${step.max} items` };
      }
      return { valid: true };
    case 'boolean':
      return { valid: typeof value === 'boolean' };
    default:
      return { valid: true };
  }
}

/**
 * Convert onboarding form state into a partial preferences object suitable
 * for `updateUserProfile` or `/api/sync-profile`. Maps step field → canonical
 * preference key.
 */
export function buildPreferencesFromForm(formState) {
  const out = {};
  for (const step of ONBOARDING_STEPS) {
    const value = formState[step.field];
    if (value === undefined || value === null) continue;
    if (Array.isArray(value) && value.length === 0) continue;
    if (typeof value === 'string' && !value.trim()) continue;
    out[step.field] = value;
  }
  return out;
}

/**
 * Initialize form state from existing canonical preferences, applying defaults
 * for missing fields. Each step's field gets a value of the right type.
 */
export function initFormFromPreferences(prefs = {}) {
  const out = {};
  for (const step of ONBOARDING_STEPS) {
    const stored = prefs[step.field];
    if (stored !== undefined && stored !== null) {
      out[step.field] = stored;
    } else {
      switch (step.type) {
        case 'multi':
        case 'tags':
          out[step.field] = [];
          break;
        case 'boolean':
          out[step.field] = false;
          break;
        case 'single':
        case 'text':
        default:
          out[step.field] = '';
      }
    }
  }
  return out;
}
